import { getMessaging, getToken, deleteToken, onMessage, isSupported } from 'firebase/messaging';
import { initFirebase, firebaseConfig } from './firebase';
import { registerPushSubscriptionAs, unregisterPushSubscriptionAs } from './pushSubscriptionWriter';

const VAPID_KEY = import.meta.env.VITE_FIREBASE_VAPID_KEY || '';

let registered = false;
/** This device's last-issued FCM token — the handle disable/rollback uses to
 *  delete exactly this device's registration. */
let currentWebToken: string | null = null;

/** Discriminated outcome of an explicit enable attempt (Phase 11).
 *  'stale' = the initiating account is no longer active — the caller must
 *  show nothing (no error, no success) and mutate nothing. `op` on the
 *  success arm is the identity-scoped operation handle, binding every
 *  follow-up step (preference write, presentation) to the initiating
 *  account. */
export type PushAttemptResult =
  | { ok: true; op?: { identityKey: string; stillValid: () => boolean } }
  | { ok: false; reason: 'unsupported' | 'blocked' | 'dismissed' | 'failed' | 'stale' };

/** Structured outcome of silent startup maintenance (Phase 11 correction). */
export type PushMaintenanceResult =
  | { status: 'registered'; rotated: boolean }
  | { status: 'skipped'; reason: 'permission' | 'unsupported' | 'stale' | 'already-maintained' }
  | { status: 'failed' };

/**
 * READ-ONLY permission/support inspection — never triggers a prompt, a
 * service-worker registration, or any network call. 'unsupported' covers
 * missing browser APIs and a missing VAPID key (push cannot work either way).
 */
export function getWebPushPermission(): 'granted' | 'denied' | 'default' | 'unsupported' {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator) || !('Notification' in window)) return 'unsupported';
  if (!VAPID_KEY) return 'unsupported';
  return Notification.permission;
}

/**
 * The explicit ENABLE pipeline — call ONLY from a user gesture (the Settings
 * toggle). Sequence: permission prompt → service worker → FCM token →
 * backend subscription registration. Never called automatically anywhere.
 */
export async function registerWebPush(identityKey: string, stillValid?: () => boolean): Promise<PushAttemptResult> {
  const state = getWebPushPermission();
  if (state === 'unsupported') return { ok: false, reason: 'unsupported' };
  if (!(await isSupported().catch(() => false))) return { ok: false, reason: 'unsupported' };
  if (state === 'denied') return { ok: false, reason: 'blocked' };

  const permission = await Notification.requestPermission();
  if (permission === 'denied') return { ok: false, reason: 'blocked' };
  if (permission !== 'granted') return { ok: false, reason: 'dismissed' };

  const acquired = await acquireTokenAndRegister(identityKey, stillValid);
  if (acquired === 'stale') return { ok: false, reason: 'stale' };
  return acquired ? { ok: true } : { ok: false, reason: 'failed' };
}

/**
 * The shared post-permission pipeline: service worker → FCM token → backend
 * subscription registration. Contains NO permission-request call — with
 * permission already 'granted', navigator.serviceWorker.register and
 * getMessaging/getToken never show browser UI (getToken would reject, not
 * prompt, if permission were somehow revoked mid-flight). The backend write
 * happens ONLY through the identity-guarded seam (pushSubscriptionWriter),
 * which verifies after token acquisition AND after credential acquisition —
 * in the same synchronous tick as the request — that the live identity still
 * matches `identityKey`, because the backend upserts token ownership to
 * whatever credentials arrive.
 */
async function acquireTokenAndRegister(identityKey: string, stillValid?: () => boolean): Promise<{ rotated: boolean } | 'stale' | null> {
  try {
    initFirebase();
    const params = new URLSearchParams(firebaseConfig as Record<string, string>);
    const registration = await navigator.serviceWorker.register(`/firebase-messaging-sw.js?${params.toString()}`);
    await navigator.serviceWorker.ready;

    const messaging = getMessaging();
    const token = await getToken(messaging, { vapidKey: VAPID_KEY, serviceWorkerRegistration: registration });
    if (!token) return null;
    if (stillValid && !stillValid()) return 'stale';   // validity after token acquisition
    const rotated = currentWebToken !== null && currentWebToken !== token;
    currentWebToken = token;

    const written = await registerPushSubscriptionAs(identityKey, token, 'web', stillValid);
    if (written === 'stale') return 'stale';
    if (written === 'failed') return null;

    if (!registered) {
      registered = true;
      onMessage(messaging, () => {});
    }
    return { rotated };
  } catch {
    return null;
  }
}

/**
 * Silent startup maintenance for an ALREADY-granted, already-opted-in device
 * (Phase 11 correction). Structurally unable to prompt: this function never
 * calls a permission-request API — it only reads Notification.permission and
 * proceeds when it is already 'granted'. It never touches push_enabled.
 */
export async function maintainWebPushRegistration(identityKey: string, stillValid?: () => boolean): Promise<PushMaintenanceResult> {
  const state = getWebPushPermission();
  if (state === 'unsupported') return { status: 'skipped', reason: 'unsupported' };
  if (state !== 'granted') return { status: 'skipped', reason: 'permission' };
  if (!(await isSupported().catch(() => false))) return { status: 'skipped', reason: 'unsupported' };

  const acquired = await acquireTokenAndRegister(identityKey, stillValid);
  if (acquired === 'stale') return { status: 'skipped', reason: 'stale' };
  if (!acquired) return { status: 'failed' };
  return { status: 'registered', rotated: acquired.rotated };
}

/**
 * The DISABLE pipeline: delete this device's backend subscription row and
 * invalidate the FCM token client-side. Returns true when the device is
 * fully unregistered (or there was nothing to unregister — including a
 * STALE operation: the initiating account switched away, so the backend
 * DELETE is silently skipped rather than sent under the new account's
 * credentials, and no local token state is touched), false when the cleanup
 * could not complete — the caller keeps the account disabled either way
 * (backend delivery is already suppressed by push_enabled = false).
 * Never prompts.
 */
export async function unregisterWebPush(identityKey: string, stillValid?: () => boolean): Promise<boolean> {
  try {
    if (getWebPushPermission() === 'unsupported') return true;

    let token = currentWebToken;
    if (!token && Notification.permission === 'granted' && (await isSupported().catch(() => false))) {
      // Recover this device's existing token WITHOUT prompting: getToken on
      // an already-granted permission with an existing SW registration shows
      // no UI. No registration → nothing was ever subscribed here.
      const registration = await navigator.serviceWorker.getRegistration('/firebase-messaging-sw.js');
      if (registration) {
        initFirebase();
        token = await getToken(getMessaging(), { vapidKey: VAPID_KEY, serviceWorkerRegistration: registration }).catch(() => null);
      }
    }
    if (!token) return true;

    const deleted = await unregisterPushSubscriptionAs(identityKey, token, stillValid);
    if (deleted === 'stale') return true;   // silent skip — never delete under another account
    if (deleted === 'failed') return false;
    initFirebase();
    await deleteToken(getMessaging()).catch(() => {});
    currentWebToken = null;
    return true;
  } catch {
    return false;
  }
}
